import moment from 'moment';
import { I18n } from 'react-redux-i18n';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import { addLessonEntries, searchApplicants, searchLessons } from 'service/api';
import { fixOrder } from 'treetabular';
import { showModal } from './modal';

// Constants - action names
export const CHANGE_PAGE = 'pv-kultu/theoryLessons/CHANGE_PAGE';

export const FIND_LESSONS = 'pv-kultu/theoryLessons/FIND_LESSONS';
export const FIND_LESSONS_SUCCESS = 'pv-kultu/theoryLessons/FIND_LESSONS_SUCCESS';
export const FIND_LESSONS_FAILED = 'pv-kultu/theoryLessons/FIND_LESSONS_FAILED';

export const UPDATE_SEARCH_TERMS = 'pv-kultu/theoryLessons/UPDATE_SEARCH_TERMS';

export const FIND_APPLICANTS = 'pv-kultu/theoryLessons/FIND_APPLICANTS';
export const FIND_APPLICANTS_SUCCESS = 'pv-kultu/theoryLessons/FIND_APPLICANTS_SUCCESS';
export const FIND_APPLICANTS_FAILED = 'pv-kultu/theoryLessons/FIND_APPLICANTS_FAILED';

export const UPDATE_LESSONS = 'pv-kultu/theoryLessons/UPDATE_LESSONS';
export const UPDATE_APPLICANTS = 'pv-kultu/theoryLessons/UPDATE_APPLICANTS';

export const ADD_ENTRIES = 'pv-kultu/theoryLessons/ADD_ENTRIES';
export const ADD_ENTRIES_SUCCESS = 'pv-kultu/theoryLessons/ADD_ENTRIES_SUCCESS';
export const ADD_ENTRIES_FAILED = 'pv-kultu/theoryLessons/ADD_ENTRIES_FAILED';

export const RESET = 'pv-kultu/theoryLessons/RESET';
export const RESET_APPLICANTS = 'pv-kultu/theoryLessons/RESET_APPLICANTS';

// Helpers

// Initial state
const initialState = {
  page: 'LESSONS',
  lessons: undefined,
  searchTerms: {
    lessonDate: moment.call(moment.locale('fi')),
    examClass: '',
  },
  applicants: undefined,
  saved: undefined,
  loading: {
    lessons: false,
    applicants: false,
    saving: false
  },
  error: {
    lessons: undefined,
    applicants: undefined,
    saving: undefined,
  },
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case FIND_LESSONS:
      return Object.assign({}, state, {
        loading: {lessons: true}
      });
    case FIND_LESSONS_SUCCESS:
      return Object.assign({}, state, {
        loading: {lessons: false},
        lessons: action.lessons
      });
    case FIND_LESSONS_FAILED:
      return Object.assign({}, state, {
        loading: {lessons: false},
        error: {lessons: action.error},
      });
    case FIND_APPLICANTS:
      return Object.assign({}, state, {
        loading: {applicants: true}
      });
    case FIND_APPLICANTS_SUCCESS:
      return Object.assign({}, state, {
        loading: {applicants: false},
        applicants: action.applicants
      });
    case FIND_APPLICANTS_FAILED:
      return Object.assign({}, state, {
        loading: {applicants: false},
        error: {applicants: action.error},
      });
    case UPDATE_LESSONS:
      return Object.assign({}, state, {
        lessons: action.lessons
      });
    case UPDATE_APPLICANTS:
      return Object.assign({}, state, {
        applicants: action.applicants
      });
    case UPDATE_SEARCH_TERMS:
      return Object.assign({}, state, {
        searchTerms: Object.assign({}, state.searchTerms, action.searchTerms)
      });
    case ADD_ENTRIES:
      return Object.assign({}, state, {
        loading: {saving: true}
      });
    case ADD_ENTRIES_SUCCESS:
      return Object.assign({}, state, {
        loading: {saving: false},
        saved: true
      });
    case ADD_ENTRIES_FAILED:
      return Object.assign({}, state, {
        loading: {saving: false},
        saved: false,
        error: {saving: action.error},
      });
    case RESET_APPLICANTS:
      return Object.assign({}, state, {
        applicants: initialState.applicants,
        page: initialState.page
      });
    case CHANGE_PAGE:
      return Object.assign({}, state, {
        page: action.page
      });
    case RESET:
      return Object.assign({}, initialState, { searchTerms: Object.assign({}, initialState.searchTerms, {examClass: state.searchTerms.examClass})
      });
    default:
      return state;
  }
}

// Actions
export function findLessonsAction(payload, lessonType) {
  return { type: FIND_LESSONS, payload, lessonType };
}

export function findLessonsSuccess(data, lessonType) {
  return { type: FIND_LESSONS_SUCCESS, lessons: data, lessonType };
}

export function updateLessons(data) {
  return {type: UPDATE_LESSONS, lessons: data};
}

export function updateSearchTerms(data) {
  return {type: UPDATE_SEARCH_TERMS, searchTerms: data};
}

export function findLessonsFailed(error) {
  return {type: FIND_LESSONS_FAILED, error};
}

export function findApplicantsAction(payload, lessonType) {
  return { type: FIND_APPLICANTS, payload, lessonType };
}

export function findApplicantsSuccess(data, lessonType) {
  return { type: FIND_APPLICANTS_SUCCESS, applicants: data, lessonType };
}

export function updateApplicants(data) {
  return {type: UPDATE_APPLICANTS, applicants: data};
}

export function findApplicantsFailed(error) {
  return {type: FIND_APPLICANTS_FAILED, error};
}

export function addEntries(payload) {
  return {type: ADD_ENTRIES, payload}
}

export function addEntriesSuccess() {
  return {type: ADD_ENTRIES_SUCCESS}
}

export function addEntriesFailed(error) {
  return {type: ADD_ENTRIES_FAILED, error}
}

export function resetTheoryLessons() {
  return {type: RESET};
}

export function resetTheoryApplicants() {
  return {type: RESET_APPLICANTS};
}

export function changePage(page) {
  return {type: CHANGE_PAGE, page};
}

// Sagas
export function* handleFindLessonsSaga(action) {
  try {
    const options = {
      contentType: 'application/json',
      queryParams: {
        theoryLessonTable: true,
      }
    };
    const payload = Object.assign({}, action.payload, { lessonType: action.lessonType });
    delete payload.lessonDate; // Don't send the date in searchTerms
    let data = yield call(searchLessons, payload, options);
    //Fixorder should retain the order of children.
    yield put(findLessonsSuccess(fixOrder({
      idField: 'lessonId'
    })(data, action.lessonType)));
  } catch (error) {
    yield put(findLessonsFailed(error));
    if (error.message === 'Unauthorized')
      yield put(showModal('LOGIN'));
  }
}

export function* handleFindApplicantsSaga(action) {
  const lessonDate = action.payload.lessonDate;
  delete action.payload.lessonDate;
  try {
    if (lessonDate === null) {
      throw new Error("invalid_lessonDate");
    }
    const options = {
      contentType: 'application/json',
      queryParams: {
        theoryLessonTable: true
      }
    };
    const payload = Object.assign({
      searchMissingLessons: true,
    }, action.payload, { lessonType: action.lessonType });
    const data = yield call(searchApplicants, payload, options);
    yield put(findApplicantsSuccess(data));
    if (!!data && data.length > 0) {
      yield put(changePage('APPLICANTS'));
    } else {
      const modalProps = {
        messageType: 'ERROR',
        message: I18n.t('message.no_applicants_found')
      };
      yield put(showModal('MESSAGE', modalProps));
    }
  } catch (error) {
    if (error.message === 'invalid_lessonDate') {
      const modalProps = {
        messageType: 'ERROR',
        message: I18n.t('message.check_date_form')
      };
      yield put(showModal('MESSAGE', modalProps));
    }
    yield put(findApplicantsFailed(error));
    if (error.message === 'Unauthorized')
      yield put(showModal('LOGIN'));
  }
}

export function* handleAddEntriesSaga(action) {
  try {
    const options = {
      contentType: 'application/json',
      queryParams: {
        lessonDate: action.payload.lessonDate.format('YYYY-MM-DD')
      }
    };
    delete action.payload.lessonDate; // Not needed anymore
    yield call(addLessonEntries, action.payload, options);
    yield put(addEntriesSuccess());
    const modalProps = {
      messageType: 'SUCCESS',
      message: I18n.t('message.add_entries_success')
    };
    yield put(showModal('MESSAGE', modalProps));
    yield put(resetTheoryLessons());
  } catch (error) {
    yield put(addEntriesFailed({error: 'ERR_ADD_ENTRIES'}));
    if (error.message === 'Unauthorized')
      yield put(showModal('LOGIN'));
    const modalProps = {
      messageType: 'ERROR',
      message: I18n.t('message.add_entries_failed')
    };
    yield put(showModal('MESSAGE', modalProps));
  }
}

function* watchTheoryLessons() {
  yield takeEvery(FIND_LESSONS, handleFindLessonsSaga);
  yield takeEvery(FIND_APPLICANTS, handleFindApplicantsSaga);
  yield takeEvery(ADD_ENTRIES, handleAddEntriesSaga);
}

export function* theoryLessonsSagas() {
  yield all([
    watchTheoryLessons(),
  ]);
}


// WEBPACK FOOTER //
// ./src/ducks/theoryLessons.js